import { useState } from 'react';
import { Card, CardContent, CardHeader } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import toast from 'react-hot-toast';
import { Input } from '@/components/Input';

const IDEAS: Array<{
  id: string; title: string; description: string; category: string;
  status: string; tags: string[];
  submitter: string; dept: string;
  votes: { upvotes: number; downvotes: number; score: number };
  comments: number; followers: number;
  reviewer: string | null; reviewNotes: string | null;
  impact: { type: string; estimatedValue: number; actualValue?: number } | null;
  createdAt: string; updatedAt: string;
}> = [];

const CATEGORIES = ['All', 'Technology', 'Culture', 'Process', 'Product', 'Other'];
const SORT_OPTIONS = ['Trending', 'Most Votes', 'Newest', 'Implemented'];

const statusConfig: Record<string, { label: string; color: string; badge: 'success' | 'warning' | 'primary' | 'default' | 'error' }> = {
  submitted: { label: 'Submitted', color: 'text-muted-foreground', badge: 'default' },
  'under-review': { label: 'Under Review', color: 'text-warning', badge: 'warning' },
  approved: { label: 'Approved', color: 'text-success', badge: 'success' },
  'in-progress': { label: 'In Progress', color: 'text-primary', badge: 'primary' },
  implemented: { label: 'Implemented', color: 'text-success', badge: 'success' },
  rejected: { label: 'Rejected', color: 'text-destructive', badge: 'error' },
};

const impactColor: Record<string, string> = { revenue: 'text-success', 'cost-savings': 'text-primary', efficiency: 'text-warning', 'customer-satisfaction': 'text-info' };

export function IdeaBox() {
  const [activeTab, setActiveTab] = useState<'board' | 'my-ideas' | 'tracking' | 'new'>('board');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('Trending');
  const [selectedIdea, setSelectedIdea] = useState<typeof IDEAS[0] | null>(null);
  const [userVotes, setUserVotes] = useState<Record<string, 'up' | 'down' | null>>({});

  const filtered = IDEAS
    .filter(i => selectedCategory === 'All' || i.category === selectedCategory)
    .sort((a, b) => sortBy === 'Most Votes' ? b.votes.upvotes - a.votes.upvotes : sortBy === 'Implemented' ? (b.status === 'implemented' ? 1 : -1) : b.votes.score - a.votes.score);

  const handleVote = (id: string, type: 'up' | 'down', e: React.MouseEvent) => {
    e.stopPropagation();
    setUserVotes(prev => ({ ...prev, [id]: prev[id] === type ? null : type }));
    toast.success(type === 'up' ? 'Upvoted!' : 'Downvoted');
  };

  if (activeTab === 'new') {
    return (
      <div className="space-y-6 max-w-2xl mx-auto">
        <div className="flex items-center gap-4">
          <Button variant="unstyled" size="unstyled" onClick={() => setActiveTab('board')} className="text-muted-foreground hover:text-primary transition-colors font-semibold">← Back to Ideas</Button>
          <h1 className="tracking-tight text-foreground font-display text-page-title">Submit a New Idea</h1>
        </div>
        <Card>
          <CardContent className="p-6 space-y-5">
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Idea Title *</label>
              <Input placeholder="Give your idea a clear, catchy title..." className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Description *</label>
              <textarea rows={6} placeholder="Describe your idea in detail. What problem does it solve? What is the expected impact?" className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none resize-y" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Category *</label>
                <select className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none">
                  {CATEGORIES.filter(c => c !== 'All').map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Expected Impact</label>
                <select className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none">
                  <option>Revenue increase</option>
                  <option>Cost savings</option>
                  <option>Efficiency gain</option>
                  <option>Customer satisfaction</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Tags (comma-separated)</label>
              <Input placeholder="e.g. automation, ai, product" className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none" />
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setActiveTab('board')}>Cancel</Button>
              <Button variant="primary" className="flex-1" onClick={() => { toast.success('Idea submitted! The team has been notified.'); setActiveTab('board'); }}>
                Submit Idea
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (selectedIdea) {
    const sc = statusConfig[selectedIdea.status];
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Button variant="unstyled" size="unstyled" onClick={() => setSelectedIdea(null)} className="hover:text-primary transition-colors font-semibold">← Ideas</Button>
          <span>/</span>
          <span className="text-foreground font-semibold">{selectedIdea.title}</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <Badge variant={sc.badge}>{sc.label}</Badge>
                  <span className="text-xs px-2 py-0.5 border border-border rounded-lg text-muted-foreground"># {selectedIdea.category}</span>
                  {selectedIdea.tags.map(t => <span key={t} className="text-xs px-1.5 py-0.5 bg-primary/10 text-primary rounded-md">{t}</span>)}
                </div>
                <h1 className="tracking-tight text-foreground mb-2 font-display text-page-title">{selectedIdea.title}</h1>
                <p className="text-sm text-muted-foreground">Submitted by <span className="font-semibold text-foreground">{selectedIdea.submitter}</span> ({selectedIdea.dept}) · {selectedIdea.createdAt}</p>
                <p className="text-sm text-foreground mt-4 leading-relaxed">{selectedIdea.description}</p>

                {selectedIdea.impact && (
                  <div className="mt-4 p-4 bg-success/5 border border-success/20 rounded-lg">
                    <p className="text-xs font-semibold text-muted-foreground mb-2">Impact</p>
                    <div className="flex gap-6">
                      <div>
                        <p className="text-xs text-muted-foreground">Type</p>
                        <p className={`font-semibold capitalize ${impactColor[selectedIdea.impact.type]}`}>{selectedIdea.impact.type.replace('-', ' ')}</p>
                      </div>
                      {selectedIdea.impact.estimatedValue && (
                        <div>
                          <p className="text-xs text-muted-foreground">Estimated Value</p>
                          <p className="font-semibold text-success">${selectedIdea.impact.estimatedValue.toLocaleString()}</p>
                        </div>
                      )}
                      {selectedIdea.impact.actualValue && (
                        <div>
                          <p className="text-xs text-muted-foreground">Actual Value</p>
                          <p className="font-semibold text-success text-lg">${selectedIdea.impact.actualValue.toLocaleString()}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedIdea.reviewNotes && (
                  <div className="mt-4 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                    <p className="text-xs font-semibold text-primary mb-1">Reviewer Notes — {selectedIdea.reviewer}</p>
                    <p className="text-sm text-foreground">{selectedIdea.reviewNotes}</p>
                  </div>
                )}

                <div className="flex item-center gap-6 mt-6 pt-4 border-t border-border">
                  <Button variant="unstyled" size="unstyled" onClick={e => handleVote(selectedIdea.id, 'up', e)}
                    className={`flex items-center gap-2 text-sm font-semibold transition-colors ${userVotes[selectedIdea.id] === 'up' ? 'text-success' : 'text-muted-foreground hover:text-success'}`}>
                    {selectedIdea.votes.upvotes + (userVotes[selectedIdea.id] === 'up' ? 1 : 0)} upvotes
                  </Button>
                  <Button variant="unstyled" size="unstyled" onClick={e => handleVote(selectedIdea.id, 'down', e)}
                    className={`flex items-center gap-2 text-sm font-semibold transition-colors ${userVotes[selectedIdea.id] === 'down' ? 'text-destructive' : 'text-muted-foreground hover:text-destructive'}`}>
                    {selectedIdea.votes.downvotes} downvotes
                  </Button>
                  <span className="text-sm text-muted-foreground">{selectedIdea.comments} comments</span>
                  <span className="text-sm text-muted-foreground">{selectedIdea.followers} following</span>
                  <Button variant="unstyled" size="unstyled" className="ml-auto text-sm font-semibold text-primary hover:text-primary" onClick={() => toast.success('Following idea...')}>+ Follow</Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="bg-card/30 px-6 py-4 flex flex-row justify-between items-center">
                <h2 className="text-foreground font-display text-section-title">Comments ({selectedIdea.comments})</h2>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {[
                  { author: 'Jane Smith', text: 'Great idea! We already have some ML infrastructure that could support this.', time: '2 days ago', upvotes: 8 },
                  { author: 'David Chen', text: 'I\'ve seen this implemented at my previous company. It reduced ticket misrouting by 70%.', time: '1 day ago', upvotes: 12 },
                ].map((c, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-xs font-semibold text-primary shrink-0">{c.author[0]}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-foreground">{c.author}</p>
                        <p className="text-xs text-muted-foreground">{c.time}</p>
                      </div>
                      <p className="text-sm text-foreground mt-1">{c.text}</p>
                      <Button variant="unstyled" size="unstyled" className="text-xs text-muted-foreground hover:text-primary transition-colors mt-1" onClick={() => toast.success('Liked comment!')}>{c.upvotes}</Button>
                    </div>
                  </div>
                ))}
                <div className="flex gap-3 mt-4">
                  <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-xs font-semibold text-primary shrink-0">Y</div>
                  <div className="flex-1 flex gap-2">
                    <Input placeholder="Add a comment..." className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" />
                    <Button variant="primary" size="sm" onClick={() => toast.success('Comment posted!')}>Post</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            <Card>
              <CardContent className="p-4 space-y-2">
                <Button variant="primary" size="sm" className="w-full" onClick={() => toast.success('Following idea!')}>Follow Idea</Button>
                <Button variant="secondary" size="sm" className="w-full" onClick={() => toast.success('Sharing idea...')}>Share</Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="bg-card/30 px-4 py-3"><p className="text-xs font-semibold text-muted-foreground">Voting Score</p></CardHeader>
              <CardContent className="p-4 text-center">
                <p className="text-warning font-display text-metric">{selectedIdea.votes.score}</p>
                <p className="text-xs text-muted-foreground mt-1">{selectedIdea.votes.upvotes} up · {selectedIdea.votes.downvotes} down</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">Idea <span className="text-foreground">Box</span></h1>
          <p className="text-muted-foreground mt-1">Innovation Management · Submit · Vote · Track Implementation</p>
        </div>
        <Button variant="primary" onClick={() => setActiveTab('new')}>+ Submit Idea</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries({ 'Total Ideas': IDEAS.length, 'Under Review': IDEAS.filter(i => i.status === 'under-review').length, 'Approved': IDEAS.filter(i => i.status === 'approved' || i.status === 'in-progress').length, 'Implemented': IDEAS.filter(i => i.status === 'implemented').length }).map(([label, value], i) => (
          <Card key={i}><CardContent className="p-5">
            <p className="text-xs text-muted-foreground font-semibold mb-1">{label}</p>
            <p className="text-primary font-display text-metric">{value}</p>
          </CardContent></Card>
        ))}
      </div>

      <div className="flex gap-2 border-b border-border">
        {[
          { id: 'board', label: 'Idea Board' },
          { id: 'my-ideas', label: 'My Ideas' },
          { id: 'tracking', label: 'Implementation Tracking' },
        ].map(t => (
          <Button variant="unstyled" size="unstyled" key={t.id} onClick={() => setActiveTab(t.id as any)}
            className={`pb-4 px-4 text-sm font-semibold transition-all relative ${activeTab === t.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
            {t.label}
            {activeTab === t.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
          </Button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex gap-2 flex-wrap">
          {CATEGORIES.map(c => (
            <Button variant="unstyled" size="unstyled" key={c} onClick={() => setSelectedCategory(c)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all ${selectedCategory === c ? 'bg-primary text-white border-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>
              {c}
            </Button>
          ))}
        </div>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="ml-auto bg-background border border-border rounded-lg px-3 py-2 text-foreground text-sm focus:outline-none">
          {SORT_OPTIONS.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div className="space-y-4">
        {(activeTab === 'my-ideas' ? filtered.filter(i => i.submitter === 'Mike Johnson') : activeTab === 'tracking' ? filtered.filter(i => ['approved', 'in-progress', 'implemented'].includes(i.status)) : filtered).map(idea => {
          const sc = statusConfig[idea.status];
          const myVote = userVotes[idea.id];
          return (
            <Card key={idea.id} className="hover:-translate-y-0.5 transition-all cursor-pointer group" onClick={() => setSelectedIdea(idea)}>
              <CardContent className="p-6">
                <div className="flex gap-4">
                  <div className="flex flex-col items-center gap-1 shrink-0">
                    <Button variant="unstyled" size="unstyled" onClick={e => handleVote(idea.id, 'up', e)}
                      className={`w-10 h-10 rounded-lg border flex items-center justify-center text-lg transition-all hover:scale-110 active:scale-95 ${myVote === 'up' ? 'bg-success/10 border-success text-success' : 'border-border text-muted-foreground hover:border-success hover:text-success'}`}>
                                          </Button>
                    <span className="text-lg font-semibold text-foreground">{idea.votes.score + (myVote === 'up' ? 1 : myVote === 'down' ? -1 : 0)}</span>
                    <Button variant="unstyled" size="unstyled" onClick={e => handleVote(idea.id, 'down', e)}
                      className={`w-10 h-10 rounded-lg border flex items-center justify-center text-lg transition-all hover:scale-110 active:scale-95 ${myVote === 'down' ? 'bg-destructive/10 border-destructive text-destructive' : 'border-border text-muted-foreground hover:border-destructive hover:text-destructive'}`}>
                                          </Button>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                          <h3 className="text-foreground group-hover:text-primary transition-colors font-display text-section-title">{idea.title}</h3>
                          <Badge variant={sc.badge}>{sc.label}</Badge>
                          <span className="text-xs px-2 py-0.5 border border-border rounded-lg text-muted-foreground">{idea.category}</span>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{idea.description}</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                          <span>{idea.submitter} ({idea.dept})</span>
                          <span>· {idea.createdAt}</span>
                          <span>· {idea.comments} comments</span>
                          <span>· {idea.followers} following</span>
                          {idea.tags.map(t => <span key={t} className="px-1.5 py-0.5 bg-primary/10 text-primary rounded-md font-semibold">{t}</span>)}
                        </div>
                      </div>
                    </div>
                    {activeTab === 'tracking' && idea.impact && (
                      <div className="mt-3 flex gap-4 text-xs">
                        <span className={`font-semibold capitalize ${impactColor[idea.impact.type]}`}>{idea.impact.type.replace('-', ' ')}</span>
                        {idea.impact.estimatedValue && <span className="text-success">Estimated: ${idea.impact.estimatedValue.toLocaleString()}</span>}
                        {idea.impact.actualValue && <span className="text-success font-semibold">Actual: ${idea.impact.actualValue.toLocaleString()}</span>}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
